import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import { cn } from '#/lib/utils';

import { mapStockReceiptMeta } from '../helper/mapStockReceiptMeta';

function displayOrDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function InfoLabel({ label, tip = null }) {
  if (!tip) {
    return <p className="text-xs text-gray-500">{label}</p>;
  }

  return (
    <p className="text-xs text-gray-500">
      <span className="inline-flex items-center gap-0.5">
        <span>{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex cursor-help items-center border-0 bg-transparent p-0',
                'text-gray-400 hover:text-primary-600',
              )}
              aria-label={`${label}說明`}
            >
              <HelpCircle size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {tip}
          </TooltipContent>
        </Tooltip>
      </span>
    </p>
  );
}

InfoLabel.propTypes = {
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
};

function ReceiptHero({ meta, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">{meta.code}</h1>
            {meta.status ? <DocumentStatusBadge status={meta.status} /> : null}
          </div>
          <p className="mt-1 text-sm text-gray-600">{meta.warehouseName}</p>
        </div>
        {meta.showInfoCardMoney ? (
          <p className="text-lg font-semibold text-gray-900">
            總金額：
            {meta.totalAmountLabel}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

ReceiptHero.propTypes = {
  meta: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
    warehouseName: PropTypes.string,
    totalAmountLabel: PropTypes.string,
    showInfoCardMoney: PropTypes.bool,
  }).isRequired,
  actions: PropTypes.node,
};

function ReceiptInfoGrid({ meta }) {
  return (
    <div
      className={cn(
        'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
        'sm:grid-cols-2 md:grid-cols-4',
      )}
    >
      <div>
        <p className="text-xs text-gray-500">建立</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.createDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">修改</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.reviseDate}</p>
      </div>
      <div>
        <p className="text-xs text-gray-500">完成</p>
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{meta.closeDate}</p>
      </div>
      <div className="hidden md:block" aria-hidden="true" />
      <div>
        <InfoLabel label="來源" tip={meta.sourceLockTip} />
        <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">
          {displayOrDash(meta.sourceTypeLabel)}
        </p>
      </div>
      {meta.showInfoCardSupplier ? (
        <div>
          <p className="text-xs text-gray-500">供應商</p>
          <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">
            {displayOrDash(meta.supplierName)}
          </p>
        </div>
      ) : null}
      {meta.showInfoCardCurrency ? (
        <div>
          <p className="text-xs text-gray-500">幣別</p>
          <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">
            {displayOrDash(meta.currencyLabel)}
          </p>
        </div>
      ) : null}
      {meta.showInfoCardMoney ? (
        <div>
          <p className="text-xs text-gray-500">關稅</p>
          <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">
            {displayOrDash(meta.customsDutyLabel)}
          </p>
        </div>
      ) : null}
      {meta.showInfoCardMoney ? (
        <div>
          <p className="text-xs text-gray-500">運費</p>
          <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">
            {displayOrDash(meta.freightLabel)}
          </p>
        </div>
      ) : null}
      <div className="hidden md:block" aria-hidden="true" />
      <div className="md:col-span-2">
        <p className="text-xs text-gray-500">事由</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.issueReason)}
        </p>
      </div>
      <div className="md:col-span-2">
        <p className="text-xs text-gray-500">備註</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.remark)}
        </p>
      </div>
    </div>
  );
}

ReceiptInfoGrid.propTypes = {
  meta: PropTypes.shape({
    sourceTypeLabel: PropTypes.string,
    sourceLockTip: PropTypes.string,
    supplierName: PropTypes.string,
    currencyLabel: PropTypes.string,
    customsDutyLabel: PropTypes.string,
    freightLabel: PropTypes.string,
    showInfoCardMoney: PropTypes.bool,
    showInfoCardSupplier: PropTypes.bool,
    showInfoCardCurrency: PropTypes.bool,
    createDate: PropTypes.string,
    reviseDate: PropTypes.string,
    closeDate: PropTypes.string,
    issueReason: PropTypes.string,
    remark: PropTypes.string,
  }).isRequired,
};

const StockReceiptMainInfoCard = ({ detail, embedded = false, actions = null }) => {
  const meta = mapStockReceiptMeta(detail);

  const content = (
    <>
      <ReceiptHero meta={meta} actions={actions} />
      <ReceiptInfoGrid meta={meta} />
    </>
  );

  if (embedded) {
    return (
      <div className="border-b border-slate-200 p-5">
        {content}
      </div>
    );
  }

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        {content}
      </div>
    </div>
  );
};

StockReceiptMainInfoCard.propTypes = {
  detail: PropTypes.shape({
    receiptNo: PropTypes.string,
    status: PropTypes.string,
    sourceType: PropTypes.string,
    sourceId: PropTypes.string,
    issueReason: PropTypes.string,
    remark: PropTypes.string,
    currency: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.string,
        code: PropTypes.string,
        name: PropTypes.string,
      }),
    ]),
    amountSummary: PropTypes.shape({
      currencyId: PropTypes.string,
      currencyCode: PropTypes.string,
      roundingMode: PropTypes.string,
      totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      customsDutyAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      freightAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    supplier: PropTypes.shape({
      id: PropTypes.string,
      code: PropTypes.string,
      name: PropTypes.string,
    }),
    amount: PropTypes.shape({
      totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      roundingMode: PropTypes.string,
    }),
    record: PropTypes.shape({
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
    }),
    history: PropTypes.shape({
      completedAt: PropTypes.string,
    }),
    warehouse: PropTypes.shape({
      displayName: PropTypes.string,
      name: PropTypes.string,
    }),
    lines: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  embedded: PropTypes.bool,
  actions: PropTypes.node,
};

export default StockReceiptMainInfoCard;
